// src/components/subcontractors/tabs/SubHistoryTab.jsx
import { useState } from 'react';
import { useSubcontractors } from '../../../context/SubcontractorContext';
import JobFormModal from '../modals/JobFormModal';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseLocalDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDate(isoStr) {
  const d = parseLocalDate(isoStr);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function buildStars(rating) {
  return Array.from({ length: 5 }, (_, i) => {
    const pos = i + 1;
    if (rating >= pos) return 'full';
    if (rating >= pos - 0.5) return 'half';
    return 'empty';
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StarDisplay({ rating }) {
  const stars = buildStars(rating);
  return (
    <div className="flex items-center gap-1">
      <span className="flex">
        {stars.map((type, i) => {
          if (type === 'full') {
            return (
              <span key={i} style={{ color: '#F59E0B', fontSize: 'var(--wm-text-cta)', lineHeight: 1 }}>★</span>
            );
          }
          if (type === 'half') {
            return (
              <span
                key={i}
                style={{ color: '#F59E0B', fontSize: 'var(--wm-text-cta)', lineHeight: 1, opacity: 0.6 }}
              >
                ★
              </span>
            );
          }
          return (
            <span key={i} style={{ color: '#94A3B8', fontSize: 'var(--wm-text-cta)', lineHeight: 1 }}>☆</span>
          );
        })}
      </span>
      <span className="text-[var(--wm-text-ui)]" style={{ color: '#7D93AE' }}>{Number(rating).toFixed(1)}</span>
    </div>
  );
}

function CompletedJobCard({ job }) {
  const { getJobPaymentSummary, updateJob, deleteJob } = useSubcontractors();
  const { amountPaid, status: paymentStatus } = getJobPaymentSummary(job);
  const [editModal, setEditModal] = useState(false);

  const handleArchive = () => {
    updateJob(job.id, { status: 'archived' });
  };

  const handleDelete = () => {
    if (window.confirm('Delete this job? This cannot be undone.')) {
      deleteJob(job.id);
    }
  };

  return (
    <div className="bg-white dark:bg-[#0F1923] rounded-xl p-4 border border-gray-200 dark:border-[#1E3048]">
      {/* Top row */}
      <div className="flex justify-between items-start gap-3">
        <div className="min-w-0">
          <p className="text-[var(--wm-text-body)] font-medium text-[#0F1923] dark:text-[#F8FAFE] truncate">{job.projectRef}</p>
          <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] truncate">{job.customerName}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Completed pill */}
          <span
            className="px-2 py-0.5 text-[var(--wm-text-ui)] rounded-full border"
            style={{ backgroundColor: '#10B98122', borderColor: '#10B981', color: '#10B981' }}
          >
            Completed
          </span>
          {/* Payment status pill */}
          <PaymentStatusPill paymentStatus={paymentStatus} />
        </div>
      </div>

      {/* Details row */}
      <div className="mt-2">
        {job.vehicleLabel && (
          <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">{job.vehicleLabel}</p>
        )}
        {job.scope && (
          <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-1 line-clamp-2">{job.scope}</p>
        )}
      </div>

      {/* Completed date */}
      {job.completedDate && (
        <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-2">
          Completed {formatDate(job.completedDate)}
        </p>
      )}

      {/* Payment summary */}
      <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-1">
        Agreed: {formatCurrency(job.agreedPrice)}&nbsp;&nbsp;•&nbsp;&nbsp;Paid: {formatCurrency(amountPaid)}
      </p>

      {/* Review section */}
      {job.review && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-[#1E3048]">
          <StarDisplay rating={job.review.rating} />
          {job.review.notes && (
            <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] italic mt-1">{job.review.notes}</p>
          )}
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span
              className="text-[var(--wm-text-ui)] px-2 py-0.5 rounded-full"
              style={
                job.review.wouldRehire
                  ? { backgroundColor: '#10B98122', color: '#10B981' }
                  : { backgroundColor: '#EF444422', color: '#EF4444' }
              }
            >
              Would rehire: {job.review.wouldRehire ? 'Yes' : 'No'}
            </span>
            {job.review.reviewedAt && (
              <span className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">
                Reviewed: {formatDate(job.review.reviewedAt)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-[#1E3048]">
        <button
          type="button"
          onClick={() => setEditModal(true)}
          aria-label={`Edit job ${job.projectRef}`}
          className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors px-2 py-1.5"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={handleArchive}
          aria-label={`Archive job ${job.projectRef}`}
          className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] hover:text-[#EF4444] transition-colors px-2 py-1.5"
        >
          Archive
        </button>
        <button
          type="button"
          onClick={handleDelete}
          aria-label={`Delete job ${job.projectRef}`}
          className="text-[var(--wm-text-ui)] text-red-400 hover:text-red-300 transition-colors px-2 py-1.5"
        >
          Delete
        </button>
      </div>

      {editModal && (
        <JobFormModal
          subcontractorId={job.subcontractorId}
          job={job}
          onClose={() => setEditModal(false)}
        />
      )}
    </div>
  );
}

function ArchivedJobCard({ job }) {
  const { getJobPaymentSummary, updateJob, deleteJob } = useSubcontractors();
  const { amountPaid } = getJobPaymentSummary(job);

  const handleRestore = () => {
    updateJob(job.id, { status: 'in_progress' });
  };

  const handleDelete = () => {
    if (window.confirm('Delete this job? This cannot be undone.')) {
      deleteJob(job.id);
    }
  };

  return (
    <div className="bg-white dark:bg-[#0F1923] rounded-xl p-4 border border-gray-200 dark:border-[#1E3048]">
      {/* Top row */}
      <div className="flex justify-between items-start gap-3">
        <div className="min-w-0">
          <p className="text-[var(--wm-text-body)] font-medium text-[#0F1923] dark:text-[#F8FAFE] truncate">{job.projectRef}</p>
          <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] truncate">{job.customerName}</p>
        </div>
        <div className="flex-shrink-0">
          {/* Archived pill */}
          <span
            className="px-2 py-0.5 text-[var(--wm-text-ui)] rounded-full border"
            style={{ backgroundColor: '#64748B22', borderColor: '#64748B', color: '#64748B' }}
          >
            Archived
          </span>
        </div>
      </div>

      {/* Details row */}
      <div className="mt-2">
        {job.vehicleLabel && (
          <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">{job.vehicleLabel}</p>
        )}
        {job.scope && (
          <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-1 line-clamp-2">{job.scope}</p>
        )}
      </div>

      {/* Payment summary */}
      <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-2">
        Agreed: {formatCurrency(job.agreedPrice)}&nbsp;&nbsp;•&nbsp;&nbsp;Paid: {formatCurrency(amountPaid)}
      </p>

      {/* Last updated date */}
      {job.updatedAt && (
        <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-1">
          Updated {formatDate(job.updatedAt)}
        </p>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-[#1E3048]">
        <button
          type="button"
          onClick={handleRestore}
          aria-label={`Restore job ${job.projectRef}`}
          className="text-[var(--wm-text-ui)] transition-colors px-2 py-1.5"
          style={{ color: '#2E8BF0' }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '0.8'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
        >
          Restore
        </button>
        <button
          type="button"
          onClick={handleDelete}
          aria-label={`Delete job ${job.projectRef}`}
          className="text-[var(--wm-text-ui)] text-red-400 hover:text-red-300 transition-colors px-2 py-1.5"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function PaymentStatusPill({ paymentStatus }) {
  const styles = {
    paid:    { bg: '#10B98122', border: '#10B981', text: '#10B981', label: 'Paid' },
    overdue: { bg: '#EF444422', border: '#EF4444', text: '#EF4444', label: 'Overdue' },
    partial: { bg: '#F59E0B22', border: '#F59E0B', text: '#F59E0B', label: 'Partial' },
    pending: { bg: '#64748B22', border: '#64748B', text: '#64748B', label: 'Pending' },
  };

  const s = styles[paymentStatus] || styles.pending;

  return (
    <span
      className="px-2 py-0.5 text-[var(--wm-text-ui)] rounded-full border"
      style={{ backgroundColor: s.bg, borderColor: s.border, color: s.text }}
    >
      {s.label}
    </span>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function SubHistoryTab({ sub }) {
  const { getSubJobs } = useSubcontractors();
  const [activeTab, setActiveTab] = useState('completed');

  const allJobs = getSubJobs(sub.id);

  const historyJobs = allJobs.filter(j => j.status === 'completed' || j.status === 'archived');

  const completedJobs = historyJobs
    .filter(j => j.status === 'completed')
    .sort((a, b) => {
      const da = parseLocalDate(a.completedDate);
      const db = parseLocalDate(b.completedDate);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return db - da;
    });

  const archivedJobs = historyJobs
    .filter(j => j.status === 'archived')
    .sort((a, b) => {
      const da = parseLocalDate(a.updatedAt);
      const db = parseLocalDate(b.updatedAt);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return db - da;
    });

  const activeStyles = 'bg-[#2E8BF022] border-[#2E8BF0] text-[#2E8BF0]';
  const inactiveStyles = 'border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE]';

  return (
    <div className="space-y-4">
      {/* Sub-tab toggle */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setActiveTab('completed')}
          className={`px-3 py-1.5 text-[var(--wm-text-ui)] font-medium rounded-lg border transition-colors ${activeTab === 'completed' ? activeStyles : inactiveStyles}`}
        >
          Completed ({completedJobs.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('archived')}
          className={`px-3 py-1.5 text-[var(--wm-text-ui)] font-medium rounded-lg border transition-colors ${activeTab === 'archived' ? activeStyles : inactiveStyles}`}
        >
          Archived ({archivedJobs.length})
        </button>
      </div>

      {/* Job list */}
      {activeTab === 'completed' ? (
        completedJobs.length === 0 ? (
          <div className="bg-white dark:bg-[#0F1923] rounded-xl p-8 border border-gray-200 dark:border-[#1E3048] text-center text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">
            No completed jobs yet.
          </div>
        ) : (
          completedJobs.map(job => (
            <CompletedJobCard key={job.id} job={job} />
          ))
        )
      ) : (
        archivedJobs.length === 0 ? (
          <div className="bg-white dark:bg-[#0F1923] rounded-xl p-8 border border-gray-200 dark:border-[#1E3048] text-center text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">
            No archived jobs.
          </div>
        ) : (
          archivedJobs.map(job => (
            <ArchivedJobCard key={job.id} job={job} />
          ))
        )
      )}
    </div>
  );
}
